import { Controller } from "@hotwired/stimulus"
import { Map, GeoJSON, CircleMarker, Control, DomUtil, TileLayer } from "leaflet"
import "leaflet/dist/leaflet.css"
import "./africa_map.css"

// Site marker colours (per individual site status).
const STATUS_COLORS = {
  operational: "#16a34a",
  maintenance: "#ca8a04",
  inactive:    "#6b7280",
  planned:     "#2563eb",
}

// Country deployment status colours (per Amp::Country#status). Kept in
// sync with Amp::Country::STATUS_COLORS on the Ruby side.
const COUNTRY_STATUS_COLORS = {
  operational: "#1B9DD9",  // blue   — "Platform Active"
  planned:     "#F4A24B",  // orange — "Platform in Development"
  unknown:     "#9ca3af",  // grey   — "Unknown"
}

// Fill for African countries with no Amp::Country record: white, overlaid
// with a diagonal hatch pattern ("Non-AMP Country").
const NON_AMP_FILL = "#ffffff"
const HATCH_ID     = "amp-nonamp-hatch"

// ISO numeric ID → { iso2, name } for African countries (Natural Earth / world-atlas)
const AFRICA = {
  12:  { iso2: "DZ", name: "Algeria" },
  24:  { iso2: "AO", name: "Angola" },
  204: { iso2: "BJ", name: "Benin" },
  72:  { iso2: "BW", name: "Botswana" },
  854: { iso2: "BF", name: "Burkina Faso" },
  108: { iso2: "BI", name: "Burundi" },
  120: { iso2: "CM", name: "Cameroon" },
  132: { iso2: "CV", name: "Cape Verde" },
  140: { iso2: "CF", name: "Central African Republic" },
  148: { iso2: "TD", name: "Chad" },
  174: { iso2: "KM", name: "Comoros" },
  178: { iso2: "CG", name: "Congo" },
  180: { iso2: "CD", name: "DR Congo" },
  262: { iso2: "DJ", name: "Djibouti" },
  818: { iso2: "EG", name: "Egypt" },
  226: { iso2: "GQ", name: "Equatorial Guinea" },
  232: { iso2: "ER", name: "Eritrea" },
  231: { iso2: "ET", name: "Ethiopia" },
  266: { iso2: "GA", name: "Gabon" },
  270: { iso2: "GM", name: "Gambia" },
  288: { iso2: "GH", name: "Ghana" },
  324: { iso2: "GN", name: "Guinea" },
  624: { iso2: "GW", name: "Guinea-Bissau" },
  384: { iso2: "CI", name: "Ivory Coast" },
  404: { iso2: "KE", name: "Kenya" },
  426: { iso2: "LS", name: "Lesotho" },
  430: { iso2: "LR", name: "Liberia" },
  434: { iso2: "LY", name: "Libya" },
  450: { iso2: "MG", name: "Madagascar" },
  454: { iso2: "MW", name: "Malawi" },
  466: { iso2: "ML", name: "Mali" },
  478: { iso2: "MR", name: "Mauritania" },
  480: { iso2: "MU", name: "Mauritius" },
  504: { iso2: "MA", name: "Morocco" },
  508: { iso2: "MZ", name: "Mozambique" },
  516: { iso2: "NA", name: "Namibia" },
  562: { iso2: "NE", name: "Niger" },
  566: { iso2: "NG", name: "Nigeria" },
  646: { iso2: "RW", name: "Rwanda" },
  678: { iso2: "ST", name: "Sao Tome and Principe" },
  686: { iso2: "SN", name: "Senegal" },
  694: { iso2: "SL", name: "Sierra Leone" },
  706: { iso2: "SO", name: "Somalia" },
  710: { iso2: "ZA", name: "South Africa" },
  728: { iso2: "SS", name: "South Sudan" },
  729: { iso2: "SD", name: "Sudan" },
  748: { iso2: "SZ", name: "Eswatini" },
  834: { iso2: "TZ", name: "Tanzania" },
  768: { iso2: "TG", name: "Togo" },
  788: { iso2: "TN", name: "Tunisia" },
  800: { iso2: "UG", name: "Uganda" },
  732: { iso2: "EH", name: "Western Sahara" },
  894: { iso2: "ZM", name: "Zambia" },
  716: { iso2: "ZW", name: "Zimbabwe" },
}

// African territories the world-atlas dataset lists WITHOUT an ISO numeric id
// (so `AFRICA[+f.id]` misses them and they'd be dropped). Matched by feature
// name instead. Somaliland is shown with Somalia's deployment status (iso2 SO)
// by default; give it its own Amp::Country record to style it separately.
const AFRICA_BY_NAME = {
  "Somaliland": { iso2: "SO", name: "Somaliland" },
}

// Minimal TopoJSON → GeoJSON feature extractor (no external lib needed)
function topoFeature(topology, objectName) {
  const obj   = topology.objects[objectName]
  const scale = topology.transform?.scale    || [1, 1]
  const trans = topology.transform?.translate || [0, 0]

  function decodeArc(idx) {
    const reversed = idx < 0
    const arc      = topology.arcs[reversed ? ~idx : idx]
    let x = 0, y = 0
    const coords = arc.map(([dx, dy]) => {
      x += dx; y += dy
      return [x * scale[0] + trans[0], y * scale[1] + trans[1]]
    })
    return reversed ? coords.reverse() : coords
  }

  function convertGeometry(geom) {
    if (!geom) return null
    if (geom.type === "Polygon") {
      return { type: "Polygon", coordinates: geom.arcs.map(ring => ring.flatMap(decodeArc)) }
    }
    if (geom.type === "MultiPolygon") {
      return { type: "MultiPolygon", coordinates: geom.arcs.map(poly => poly.map(ring => ring.flatMap(decodeArc))) }
    }
    return null
  }

  return {
    type: "FeatureCollection",
    features: (obj.geometries || []).map(geom => ({
      type:       "Feature",
      id:         geom.id,
      properties: geom.properties || {},
      geometry:   convertGeometry(geom),
    })).filter(f => f.geometry),
  }
}

// HTML-escapes a string for safe interpolation into tooltip markup.
function esc(s) {
  return String(s ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;")
}

// Thousands-separated integer/float for display.
function fmtNum(v) {
  if (v == null || v === "") return null
  const n = Number(v)
  if (!isFinite(n)) return null
  return n.toLocaleString(undefined, { maximumFractionDigits: 2 })
}

export default class extends Controller {
  static targets = ["map", "legend", "detail"]
  static values = {
    countries:    Object,
    ampCountries: Object,
    labels:       Object,
    sites:        Array,
  }

  connect() {
    // Prefer the dedicated map target; fall back to the controller element so
    // an older mount (no map/legend/detail targets) still renders a map.
    this._mapEl = this.hasMapTarget ? this.mapTarget : this.element
    this._map = new Map(this._mapEl, {
      zoomControl:     true,
      scrollWheelZoom: true,
      zoomSnap:        0.25,
      zoomDelta:       0.5,
    })

    // OpenStreetMap basemap below the country polygons (attribution shown per
    // OSM usage policy). Country fills are softened so it stays visible.
    new TileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      maxZoom:     18,
    }).addTo(this._map)

    this._layersByIso = {}
    this._selectedIso = null
    this._showLegend()

    // The map container is flex: 1 inside a stretched flex row, so its height
    // tracks the sidebar. When the sidebar grows / shrinks (e.g. the user
    // clicks a country and the detail panel opens), Leaflet needs to
    // invalidate its cached size or the tile pane and centre drift.
    if (typeof ResizeObserver !== "undefined") {
      this._resizeObs = new ResizeObserver(() => {
        clearTimeout(this._resizeTimer)
        this._resizeTimer = setTimeout(() => this._map?.invalidateSize({ animate: false }), 100)
      })
      this._resizeObs.observe(this._mapEl)
    }

    fetch("https://cdn.jsdelivr.net/npm/world-atlas@2/countries-50m.json")
      .then(r => r.json())
      .then(world => this._render(world))
      .catch(e => console.error("Africa map: failed to load world data", e))
  }

  disconnect() {
    this._resizeObs?.disconnect()
    clearTimeout(this._resizeTimer)
    this._map?.remove()
    this._map = null
  }

  _render(world) {
    const all = topoFeature(world, "countries")
    const africaGeo = {
      ...all,
      features: all.features.filter(f => this._info(f)),
    }

    const layer = new GeoJSON(africaGeo, {
      style:         f => this._style(f),
      onEachFeature: (f, l) => this._bind(f, l),
    }).addTo(this._map)

    // Fill non-AMP countries with the diagonal hatch <pattern> defined in the
    // view. Re-applied on zoom in case Leaflet re-paints the paths.
    this._geoLayer = layer
    this._paintHatch()
    this._map.on("zoomend", () => this._paintHatch())

    // Default view: fit to data countries, fall back to all Africa. Stored so
    // the reset control can fly back to it.
    const dataFeatures = africaGeo.features.filter(f => this._countryRecord(f))
    const boundsSource = dataFeatures.length > 0 ? dataFeatures : africaGeo.features
    this._defaultBounds = new GeoJSON({ type: "FeatureCollection", features: boundsSource }).getBounds()
    this._map.fitBounds(this._defaultBounds, { padding: [20, 20] })

    this._addResetControl()
    this._renderMarkers()
  }

  _renderMarkers() {
    const sites = this.sitesValue || []
    sites.forEach(site => {
      if (site.lat == null || site.lng == null) return
      const color = STATUS_COLORS[site.status] || "#6b7280"
      new CircleMarker([site.lat, site.lng], {
        radius:      7,
        fillColor:   color,
        color:       "#fff",
        weight:      2,
        opacity:     1,
        fillOpacity: 0.9,
      }).addTo(this._map)
    })
  }

  // Adds a "Reset to Africa" button below the zoom control.
  _addResetControl() {
    const labels = this.labelsValue || {}
    const ctl = new Control({ position: "topleft" })
    ctl.onAdd = () => {
      const btn = DomUtil.create("button", "amp-map-reset leaflet-bar")
      btn.type = "button"
      btn.title = labels.reset || "Reset to Africa"
      btn.setAttribute("aria-label", btn.title)
      btn.innerHTML = "&#x2302;" // house/home glyph; styled via CSS
      btn.addEventListener("click", e => {
        e.preventDefault()
        e.stopPropagation()
        this._resetView()
      })
      return btn
    }
    ctl.addTo(this._map)
  }

  _resetView() {
    this._clearSelection()
    this._showLegend()
    if (this._defaultBounds) {
      this._map.flyToBounds(this._defaultBounds, { padding: [20, 20], duration: 0.6 })
    }
  }

  // Fills every non-AMP country path with the diagonal hatch <pattern> defined
  // in the view (referenced as fill="url(#amp-nonamp-hatch)").
  _paintHatch() {
    this._geoLayer?.eachLayer(l => {
      if (!this._countryRecord(l.feature) && l._path) {
        l._path.setAttribute("fill", `url(#${HATCH_ID})`)
      }
    })
  }

  // Resolves the country/territory descriptor for a feature: by ISO numeric id,
  // or by name for id-less territories (e.g. Somaliland).
  _info(feature) {
    return AFRICA[+feature.id] || AFRICA_BY_NAME[feature.properties?.name] || null
  }

  // Display name: id-less territories always use their geographic name; others
  // prefer the admin-curated record name.
  _displayName(feature, record) {
    const info = this._info(feature)
    if (isNaN(+feature.id)) return info?.name || ""
    return record?.name || info?.name || ""
  }

  // Returns the Amp::Country record for this feature, or null if none exists.
  _countryRecord(feature) {
    const info = this._info(feature)
    if (!info) return null
    return this.ampCountriesValue?.[info.iso2] || null
  }

  // Live site count from the legacy countries hash (fallback when the admin
  // amp_sites_count is blank).
  _liveSiteCount(feature) {
    const info = this._info(feature)
    if (!info) return 0
    return this.countriesValue?.[info.iso2] || this.countriesValue?.[info.name] || 0
  }

  _style(feature) {
    const record   = this._countryRecord(feature)
    const selected = record && record.iso2 === this._selectedIso
    return {
      fillColor:   record ? (COUNTRY_STATUS_COLORS[record.status] || COUNTRY_STATUS_COLORS.unknown) : NON_AMP_FILL,
      weight:      selected ? 3 : (record ? 1.5 : 1),
      // White separators between coloured AMP fills; a defined grey outline for
      // non-AMP countries so every country boundary reads (matches the legend).
      color:       selected ? "#0f3d56" : (record ? "#ffffff" : "#9ca3af"),
      // AMP fills are softened so the OSM basemap shows through; non-AMP stays
      // more opaque so the "Non-AMP" hatch stays legible over the basemap.
      fillOpacity: selected ? 0.85 : (record ? 0.6 : 0.92),
    }
  }

  _bind(feature, layer) {
    const info = this._info(feature)
    if (!info) return

    const record = this._countryRecord(feature)
    if (record) this._layersByIso[record.iso2] = layer

    // Brief hover tooltip (name + status).
    const status = record ? (record.status_label || record.status) : (this.labelsValue?.no_record || "")
    layer.bindTooltip(
      `<strong>${esc(this._displayName(feature, record))}</strong>` + (status ? `<br>${esc(status)}` : ""),
      { sticky: true, direction: "top", offset: [0, -8], className: "amp-country-tooltip" }
    )

    // Rich click popup with the national-platform link.
    layer.bindPopup(this._popupHtml(feature, info), {
      maxWidth:    260,
      autoPan:     true,
      closeButton: true,
      className:   "amp-country-popup",
    })

    // Hover restyle only for AMP countries — re-applying setStyle on a non-AMP
    // layer would overwrite its hatch fill with the plain white fillColor.
    if (record) {
      layer.on("mouseover", () => { if (record.iso2 !== this._selectedIso) layer.setStyle({ fillOpacity: 0.85, weight: 2 }) })
      layer.on("mouseout",  () => { if (record.iso2 !== this._selectedIso) layer.setStyle(this._style(feature)) })
    }
    layer.on("click", () => this._selectCountry(feature, info))
  }

  _selectCountry(feature, info) {
    const record = this._countryRecord(feature)
    this._clearSelection()
    if (!record) {
      this._showLegend()
      return
    }
    this._selectedIso = record.iso2
    this._layersByIso[record.iso2]?.setStyle(this._style(feature))
    this._showDetail(record, feature)
  }

  _clearSelection() {
    const prev = this._selectedIso
    this._selectedIso = null
    if (prev && this._layersByIso[prev]) {
      const l = this._layersByIso[prev]
      l.setStyle(this._style(l.feature))
    }
  }

  // --- Contextual info panel (sidebar) --------------------------------------

  _showLegend() {
    if (this.hasLegendTarget) this.legendTarget.hidden = false
    if (this.hasDetailTarget) { this.detailTarget.hidden = true; this.detailTarget.innerHTML = "" }
  }

  _showDetail(record, feature) {
    if (!this.hasDetailTarget) return
    const L = this.labelsValue || {}
    const rows = []

    const sites = record.amp_sites_count != null ? record.amp_sites_count : this._liveSiteCount(feature)
    const push = (label, value) => { if (value != null && value !== "") rows.push([label, value]) }

    push(L.sites || "Sites", sites != null ? `${fmtNum(sites)} ${L.amp_sites || "AMP sites"}` : null)
    push(L.capacity || "Capacity",          record.capacity_kwp != null ? `${fmtNum(record.capacity_kwp)} kWp` : null)
    if (record.potential_sites != null && Number(record.potential_sites) > 0) {
      push(L.potential_sites || "Potential sites", fmtNum(record.potential_sites))
    }
    push(L.beneficiaries || "Beneficiaries", fmtNum(record.beneficiaries))
    push(L.carbon || "Carbon",               record.carbon_kg != null ? `${fmtNum(record.carbon_kg)} kg` : null)
    push(L.pue || "PUE",                     fmtNum(record.pue))
    push(L.tariff || "Tariff",               record.tariff != null ? `${fmtNum(record.tariff)} ${esc(record.currency || "")}`.trim() : null)

    const rowsHtml = rows.map(([k, v]) =>
      `<div class="amp-panel-row"><span class="amp-panel-key">${esc(k)}:</span> ` +
      `<span class="amp-panel-val">${esc(v)}</span></div>`
    ).join("")

    const swatch = `<span class="amp-panel-swatch" style="background:${esc(record.color)}"></span>`
    const link = record.platform_url
      ? `<a class="amp-panel-link" href="${esc(record.platform_url)}" target="_blank" rel="noopener">` +
        `${esc(L.view_national_platform || "View National Platform")}</a>`
      : ""
    const description = record.description
      ? `<div class="amp-panel-desc">${esc(record.description)}</div>`
      : ""

    this.detailTarget.innerHTML =
      `<div class="amp-panel-head">${swatch}<span class="amp-panel-country">${esc(this._displayName(feature, record))}</span></div>` +
      `<div class="amp-panel-status">${esc(record.status_label || record.status)}</div>` +
      rowsHtml + description + link

    this.detailTarget.hidden = false
    if (this.hasLegendTarget) this.legendTarget.hidden = true
  }

  // Builds the rich click-popup: name, sites (active) + capacity, link.
  _popupHtml(feature, info) {
    const L      = this.labelsValue || {}
    const record = this._countryRecord(feature)
    const name   = esc(this._displayName(feature, record))

    if (!record) {
      return `<div class="amp-popup"><div class="amp-popup-name">${name}</div>` +
             `<div class="amp-popup-muted">${esc(L.no_record || "No deployment record")}</div></div>`
    }

    const rows = []
    const sites = record.amp_sites_count != null ? record.amp_sites_count : this._liveSiteCount(feature)
    if (sites != null) rows.push(`<div><strong>${esc(L.sites || "Sites")}:</strong> ${fmtNum(sites)} ${esc(L.sites_active || "Active")}</div>`)
    if (record.capacity_kwp != null) rows.push(`<div><strong>${esc(L.capacity || "Capacity")}:</strong> ${fmtNum(record.capacity_kwp)} kWp</div>`)
    if (record.potential_sites != null && Number(record.potential_sites) > 0) {
      rows.push(`<div><strong>${esc(L.potential_sites || "Potential sites")}:</strong> ${fmtNum(record.potential_sites)}</div>`)
    }

    const link = record.platform_url
      ? `<a class="amp-popup-link" href="${esc(record.platform_url)}" target="_blank" rel="noopener">` +
        `${esc(L.view_national_platform || L.visit_platform || "View National Platform")}</a>`
      : ""

    const description = record.description
      ? `<div class="amp-popup-desc">${esc(record.description)}</div>`
      : ""

    const swatch = `<span class="amp-popup-swatch" style="background:${esc(record.color)}"></span>`
    return `<div class="amp-popup">` +
           `<div class="amp-popup-name">${swatch}${name}</div>` +
           rows.join("") + description + link +
           `</div>`
  }
}
